/**
 * TenderChat — the bid conversation (tender chat), scoped to one bid.
 *
 * Two layouts. `page`: message area (left) + history sidebar or docked
 * citation preview (right), as on the retired Tender Chat tab. `dock`: the
 * conversation alone, for the assistant dock (R4 §2.8), which shows history
 * in place of the conversation and opens citation previews as the modal —
 * a 400px panel has no room for a second docked reader.
 * Uses useTenderChatStore for all state.
 */

import { useState, useEffect } from "react"
import { ChatErrorBanner } from "@/components/chat/ChatErrorBanner"
import { useTenderChatStore } from "@/stores/tender-chat-store"
import { TenderChatMessageArea } from "@/components/tender-chat/TenderChatMessageArea"
import { ChatComposer } from "@/components/chat/ChatComposer"
import { TenderChatEmptyState, TenderChatSuggestions } from "@/components/tender-chat/TenderChatEmptyState"
import { TenderChatHistorySidebar } from "@/components/tender-chat/TenderChatHistorySidebar"
import { ChatDocumentPanel } from "@/components/chat/ChatDocumentPanel"
import { usePreviewStore } from "@/stores/preview-store"
import { useDockHost, useDockedPreview } from "@/hooks/useDockedPreview"
import { DRAFT_STASH_KEYS } from "@/lib/draft-stash"
import { cn } from "@/lib/utils"

interface TenderChatProps {
  bidId: string
  readOnly?: boolean
  layout?: 'page' | 'dock'
}

export function TenderChat({ bidId, readOnly = false, layout = 'page' }: TenderChatProps) {
  const isDock = layout === 'dock'
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(true)
  const { setBidId, messages, sendMessage, cancelStream, isLoading, isStreaming, error, clearError, reloadActiveThread } = useTenderChatStore()
  // This page renders the docked reader, so chat previews may dock here.
  useDockHost('chat', !isDock)
  const shouldDock = useDockedPreview() && !isDock

  // Close any docked chat preview when leaving the page, so it doesn't
  // reappear as a full-screen modal on the next route (no dock host there).
  useEffect(() => () => { usePreviewStore.getState().closePreview() }, [])

  // Scope store to this bid
  useEffect(() => {
    setBidId(bidId)
  }, [bidId, setBidId])

  // Cleanup orphaned streaming placeholders periodically
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now()
      const state = useTenderChatStore.getState()

      const orphanedPlaceholders = state.messages.filter((m) => {
        if (!m.isStreaming) return false
        const age = now - new Date(m.createdAt).getTime()
        return age > 90_000
      })

      if (orphanedPlaceholders.length > 0) {
        useTenderChatStore.setState({
          messages: state.messages.filter((m) => !orphanedPlaceholders.includes(m)),
          isStreaming: false,
        })
      }
    }, 30000)

    return () => clearInterval(cleanupInterval)
  }, [])

  // Reload the active thread when the user returns to the tab
  useEffect(() => {
    const handleFocus = () => {
      const state = useTenderChatStore.getState()
      if (state.sessionId && !state.isStreaming) {
        void reloadActiveThread()
      }
    }

    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [reloadActiveThread])

  const hasMessages = messages.length > 0

  const handleRetry = () => {
    const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user')
    if (lastUserMsg) {
      clearError()
      void sendMessage(lastUserMsg.content)
    }
  }

  const canRetry = messages.some((m) => m.role === 'user')
  const errorBanner = (
    <ChatErrorBanner error={error} onRetry={canRetry ? handleRetry : undefined} onDismiss={clearError} />
  )

  return (
    <div className={cn("flex h-full overflow-hidden", !isDock && "bg-card")}>
      {/* Main Chat Area */}
      <div className={cn("flex-1 flex flex-col", isDock ? "min-w-0" : "min-w-[320px]")}>
        {/* One stable tree position for the composer across the empty↔has-messages
            flip: swapping whole layout branches remounts the ChatComposer and loses
            its local draft — including the SharePoint-connect stash restore, which
            is one-shot. Only wrapper classes may change with hasMessages.
            `min-h-0` is load-bearing: as a column flex item this wrapper defaults
            to `min-height: auto`, which floors it at the message list's content
            height, so a streaming answer grows it past the panel and pushes the
            composer below the fold.
            The empty state centres with auto margins (`mt-auto`/`mb-auto`), NOT
            `justify-center`: centred content that outgrows the pane — at browser
            zoom, or on a short window — splits its overflow evenly and puts the
            logo and heading above the top edge, unreachable because the row above
            is `overflow-hidden`. Auto margins collapse to 0 once free space runs
            out, so the block falls back to top-aligned and `overflow-y-auto`
            scrolls it. */}
        <div
          className={cn(
            "flex-1 flex flex-col min-h-0",
            !hasMessages && "items-center overflow-y-auto px-4 py-8"
          )}
        >
          {hasMessages ? (
            <div className="flex-1 overflow-hidden">
              <TenderChatMessageArea />
            </div>
          ) : (
            <div className="mt-auto">
              <TenderChatEmptyState bidId={bidId} />
            </div>
          )}
          <div className={cn(!hasMessages && "w-full mt-8 mb-auto")}>
            {errorBanner}
            {!hasMessages && <TenderChatSuggestions />}
            <ChatComposer
              sendMessage={sendMessage}
              cancelStream={cancelStream}
              isLoading={isLoading}
              isStreaming={isStreaming}
              label="Ask about the tender documents"
              placeholder="Ask about this tender"
              draftStashKey={DRAFT_STASH_KEYS.tender}
              readOnly={readOnly}
              fullWidth={isDock}
              inputSurface={isDock ? 'card' : 'muted'}
            />
          </div>
        </div>
      </div>

      {/* Docked citation preview (wide screens) — takes the right side; the
          history sidebar is hidden while it's open and returns when closed. */}
      {isDock ? null : shouldDock ? (
        <ChatDocumentPanel />
      ) : (
        <TenderChatHistorySidebar
          isExpanded={isHistoryExpanded}
          onExpandedChange={setIsHistoryExpanded}
        />
      )}
    </div>
  )
}
